import logger from '../../utils/logger-rum';
import React, {
	useMemo,
	useCallback,
	useState,
	useRef,
	useEffect,
	useContext,
} from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import classNames from 'classnames';
import { useMachine } from '@xstate/react';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import {
	MediaItem,
	PagedMediaItemExisting,
	PagedMediaItemNew,
	PresentedMediaItem,
	VideoState,
} from '../../stores/reducers/media';
import Icon, {
	COLORS,
	ICONS,
} from '../general-components/icon';
import MediaVideoTimer from '../general-components/media-video-timer';
import {
	getVideoTimeAsHHMMSS, isAudio,
	isVideo,
} from '../../utils/utils';
import * as keyListeners from '../../utils/keyboard-handler';
import { findMediaPageBy } from '../../utils/media';
import useMedia from '../../hooks/use-media';
import useEchoCancellation, {
	shouldDoEchoCancel,
} from '../../hooks/use-echo-cancellation';
import { useAudioSink } from '../../hooks/devices/use-audio-sink';
import useShots from '../../contexts/shots/provider';
import * as MediaActions from '../../stores/actions/media';
import { showAlert } from '../general-components/alert-service';
import Portal from '../general-components/portal';
import { videoGridClasses } from '../../types/constants';
import {
	videoMachine,
	actions,
	videoActionCommands,
	initialContext,
} from '../../utils/video-machine';
import usePrevious from '../../utils/use-previous';
import {
	getCurrentPageThumb,
	getShotFeedVideoInfo,
	getVideoDuration,
	pageExists,
	VideoFeedTypes,
} from '../../utils/media';
import V_LAYOUTS from '../../utils/video-layout';
import MediaVideo from '../general-components/media-video';
import {
	DRAG_TYPE,
	DropCallback,
} from '../../stores/reducers/user-interface';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import FeedMediaContextMenu from './feed-media-context-menu';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import { ResourcePoolContext } from '../../hooks/use-resource-pool';
import {
	useVideoMachine,
	VideoMachineActionType,
} from '../../contexts/video-machine';
import { MediaControlsContext, useGetMediaControls } from '../../contexts/media-controls';
import { Shot } from '../../stores/reducers/shots';
import PlayButton from '../media/controls/play-button';
import TooltipWrapper from '../general-components/tooltips/tooltip-wrapper';
import { FkTable, GeneratedContent, ContentServiceJobStatus } from '../../stores/types/content';
import VideoHoverOptions from './video-hover-options';
import { STATIC_ASSET_URL } from '../../utils/content-templates';
import { StylePackContentType } from '../types/stylepacks';
import MediaAudio from '../general-components/media-audio';

const {
	LOADED,
	FAIL,
	TIMING,
	TIMECHANGE,
	MUTE,
	UNMUTE,
	VOLUME,
	PLAY,
	PAUSE,
	SCRUB,
	STOPSCRUBBING,
	END,
	UPDATE_DURATION,
} = videoActionCommands;

const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

interface MediaStateUpdate {
	paused?: boolean,
	timeElapsed?: number | null,
	volume?: string,
	muted?: boolean,
	mediaPage?: PagedMediaItemExisting | null,
}

interface Props {
	mediaId: number;
	feedPosition: number;
	socket: Socket | null;
	selectedMediaFeed?: number;
	onSelectMediaFeedChange?: React.Dispatch<React.SetStateAction<number | undefined>>;
	isForMainVideoEditingComponent?: boolean;
	iterateLoadedMediaCount: Function;
	isEditingFeed: boolean;
	isForShotEditorModal?: boolean;
	dropCallback: DropCallback;
	audioOnly?: boolean;
	showbuilderMediaItem?: PresentedMediaItem;
	updateShowbuilderMedia?: (i: PresentedMediaItem, v?: VideoState) => void;
	setShotModalStylepackDrawer?: (stylepackDrawer: any) => void;
}

const doEchoCancel = shouldDoEchoCancel();

export default function FeedMedia(props: Props) {
	const {
		mediaId,
		feedPosition,
		socket,
		onSelectMediaFeedChange = () => { },
		isForMainVideoEditingComponent = false,
		iterateLoadedMediaCount,
		isEditingFeed = false,
		dropCallback,
		audioOnly,
		showbuilderMediaItem,
		updateShowbuilderMedia = () => {},
		setShotModalStylepackDrawer,
	} = props;

	const isForShotEditorModal = props.isForShotEditorModal ? props.isForShotEditorModal : false;
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const session = useSelector((s: GS) => s.SessionReducer.session);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const meetingMedia = useSelector((s: GS) => s.MediaReducer.media);
	const shotModalMedia = useSelector((s: GS) => s.MediaReducer.shotModalMedia);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector((s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const globalIsDragging = useSelector((s: GS) => s.UserInterfaceReducer.globalIsDragging);
	const mediaModalOpen = useSelector((s: GS) => s.MediaReducer.mediaModalOpen);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const mediaPages = useSelector((s: GS) => s.MediaReducer.mediaPages);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const generatedContentEdits = useSelector((s: GS) => s.GeneratedContentReducer.generatedContentEdits);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);

	const {
		canEditOrControlMedia,
		canDeleteMedia,
	} = useMedia();

	const {
		currentShot,
		isOnCanvas,
		handleSelectShot,
	} = useShots();

	const {
		shot_id: shotId,
		shot_data: {
			shotFeeds,
			forcedLayout,
			autoplay,
			autoDuration,
		},
	} = currentShot;

	const media = useMemo(
		() => (isForShotEditorModal ? shotModalMedia : meetingMedia),
		[isForShotEditorModal, shotModalMedia, meetingMedia],
	);

	const { addMediaControls, removeMediaControlsById } = useContext(MediaControlsContext);
	const currentMediaControl = useGetMediaControls(`${mediaId}${isForShotEditorModal ? '-draft' : ''}`);

	const [mouseoverValue, setMouseoverValue] = useState(0);
	const [showBackground, setShowBackground] = useState<boolean>(false);
	const [duration, setDuration] = useState<number>(0);
	const lastPlayTime = useRef(0);
	const [playbackWarningShown, setPlaybackWarningShown] = useState<boolean>(false);
	const [isHovering, setIsHovering] = useState<boolean>(false);
	const [readyForInput, setReadyForInput] = useState(false);
	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();
	const [bubblePos, setBubblePos] = useState(0);

	const dispatch = useDispatch();
	const showVideoPlaybackWarning = useRef(false);
	const videoElRef = useRef<HTMLVideoElement>(null);
	const audioElRef = useRef<HTMLAudioElement>(null);
	const echoCancelAudioRef = useRef<HTMLAudioElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const { applySinkId } = useAudioSink();
	const resourcePool = useContext(ResourcePoolContext);
	const { videoMachineDispatch } = useVideoMachine();

	const timeBubbleRef = useRef<HTMLDivElement>(null);
	const scrubBarRef = useRef<HTMLInputElement>(null);

	const initialLoad = useRef<boolean>(true);
	const start = useRef<number | null>(null);
	// used to sync the start time of new videos with paginated media
	const forceFireSyncOnAutoplay = useRef<boolean>(false);
	const userPaused = useRef(true);

	const onMouseMoveDebounce = useRef<NodeJS.Timeout>();
	const onInputDebounce = useRef<NodeJS.Timeout>();
	const onChangeDebounce = useRef<NodeJS.Timeout>();

	// FeedMedia is used for media being presented as well as media in Showbuilder which is why we use
	// both of these variables
	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);

	const numberOfPages = useMemo(() => mediaItem?.media_data.image_array.length ?? 0, [mediaItem?.media_data.image_array]);

	// does this media item have an associated generated content item?
	const mediaGeneratedContent = useMemo(() => {
		if (!generatedContent) {
			return undefined;
		}

		// first check generatedContentEdits for shot modal and fall back to generatedContent if it can't be found
		if (isForShotEditorModal) {
			const gcEditItem = generatedContentEdits.find((t) => (t.fkId === mediaId && t.fkTable === FkTable.media));
			if (gcEditItem) {
				return gcEditItem;
			}
		}

		return generatedContent.find((t: GeneratedContent) => (t.fkId === mediaId && t.fkTable === FkTable.media));
	}, [mediaId, generatedContentEdits, generatedContent]);
	
	const mediaGeneratedContentTemplate = useMemo(() => {
		if (mediaGeneratedContent) {
			return contentTemplates.find((t) => t.id === mediaGeneratedContent.templateId);
		}

		if (mediaItem) {
			// non-edited media items image will match a template
			return contentTemplates.find((t) => t.videoUrl
				?.replace('ASSET_URL', STATIC_ASSET_URL ?? '') === mediaItem?.images);
		}
	}, [mediaGeneratedContent, mediaItem, contentTemplates]);

	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);

	const urlOfCurrentPage = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);

	// Detect countdown content to stabilize video URL during playback
	const isCountdownContent = useMemo(
		() => mediaGeneratedContentTemplate?.contentType === StylePackContentType.COUNTDOWN,
		[mediaGeneratedContentTemplate?.contentType]
	);

	// Stable URL: prevents video rewind when server regenerates countdown content mid-playback
	const [stableVideoUrl, setStableVideoUrl] = useState(urlOfCurrentPage);
	const pendingVideoUrl = useRef<string | null>(null);
	const jobStatus = mediaGeneratedContent?.jobStatus;

	// Defer URL changes for countdown content during active playback
	// Only defer the final CDN URL (jobStatus=completed) — allow Capsule render preview through
	useEffect(() => {
		const isActivelyPlaying = !videoElRef.current?.paused;
		const isFinalGeneratedVideo = jobStatus === ContentServiceJobStatus.completed;

		if (!isCountdownContent) {
			pendingVideoUrl.current = null;
			setStableVideoUrl(urlOfCurrentPage);
			return;
		}

		// Only defer the final generated video URL, not the Capsule render preview
		if (isActivelyPlaying && isFinalGeneratedVideo && stableVideoUrl && urlOfCurrentPage !== stableVideoUrl) {
			pendingVideoUrl.current = urlOfCurrentPage;
		} else {
			setStableVideoUrl(urlOfCurrentPage);
			pendingVideoUrl.current = null;
		}
	}, [urlOfCurrentPage, isCountdownContent, jobStatus]);

	// HLS detection for the stable URL (used only on <MediaVideo>)
	const stableIsHlsVideo = useMemo(
		(): boolean => stableVideoUrl?.endsWith('.m3u8'),
		[stableVideoUrl]
	);

	const currentPageIsVideo = useMemo(() => isVideo(urlOfCurrentPage), [urlOfCurrentPage]);
	const currentPageIsAudio = useMemo(() => isAudio(urlOfCurrentPage), [urlOfCurrentPage]);

	const currentMediaPage = useMemo(
		() => findMediaPageBy(mediaPages, urlOfCurrentPage, presentedMediaItem?.mediaId),
		[mediaPages, urlOfCurrentPage, presentedMediaItem?.mediaId],
	);

	const currentMediaPageVolume = useMemo((): number => {
		const curVolume = currentMediaPage?.volume;

		if (Number.isNaN(Number(curVolume)) || curVolume === null) {
			return 1;
		}

		return Number(curVolume);
	}, [currentMediaPage?.volume]);

	const [current, send] = useMachine(videoMachine, {
		actions: { ...actions },
		context: {
			...initialContext,
			shownVolume: currentMediaPageVolume,
		},
	});

	useEffect(() => () => {
		removeMediaControlsById(`${mediaId}${isForShotEditorModal ? '-draft' : ''}`);
	}, [mediaId]);

	useEffect(() => {
		if (audioElRef.current && echoCancelAudioRef.current) {
			const ref = echoCancelAudioRef.current as any
			ref.mirror =  audioElRef.current;
		}
	}, [audioElRef.current, echoCancelAudioRef.current]);

	const {
		elapsed,
		volume,
		paused,
		shownVolume,
		muted: videoMachineMuted,
		video,
	} = current.context;
	const bufferedDuration = video?.duration ? video?.duration : 0;
	const muted = isForShotEditorModal
		? true
		: currentMediaPage?.muted ?? videoMachineMuted;

	// Flush pending countdown URL when playback stops
	useEffect(() => {
		if (!isCountdownContent || !pendingVideoUrl.current) return;

		if (videoElRef.current?.paused) {
			setStableVideoUrl(pendingVideoUrl.current);
			pendingVideoUrl.current = null;
		}
	}, [paused, isCountdownContent]);

	// Flush pending countdown URL on shot change
	useEffect(() => {
		if (pendingVideoUrl.current) {
			setStableVideoUrl(pendingVideoUrl.current);
			pendingVideoUrl.current = null;
		}
	}, [shotId]);

	// our timer timmy
	const timmy = useRef<NodeJS.Timer>(null);

	const adjustedVolume = useCallback(
		(volumeIncoming: number) => (
			(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
				? volumeIncoming
				: volumeIncoming * backstageCanvasVolume
		),
		[backstageCanvasVolume, isOnCanvas, embed, roomState],
	);

	useEffect(() => {
		if (!isForShotEditorModal && !currentMediaPage?.muted) {
			send(VOLUME, {
				shownVolume, adjustedVolume, from: 'update-volume-useEfffect',
			});
		}
	}, [isOnCanvas, backstageCanvasVolume, shownVolume]);

	function checkVideoPlaybackLogic() {
		if (!paused) {
			// @ts-ignore
			if (
				videoElRef.current
				&& videoElRef.current.currentTime === lastPlayTime.current
				&& videoElRef.current.ended) {
				if (!showVideoPlaybackWarning.current) {
					// don't need to keep setting it
					showVideoPlaybackWarning.current = true;
				}
			} else if (showVideoPlaybackWarning.current) { // update it back to not show video is playing
				showVideoPlaybackWarning.current = false;
			}
			// @ts-ignore
			if (videoElRef.current) { lastPlayTime.current = videoElRef.current.currentTime; }
		} else {
			showVideoPlaybackWarning.current = false;
		}
	}

	useEffect(() => {
		if (!videoElRef.current?.paused && !timmy.current) {
			lastPlayTime.current = 0.1;
			// @ts-ignore
			timmy.current = setInterval(checkVideoPlaybackLogic, 5000);
		} else if (videoElRef.current?.paused && timmy.current) {
			clearInterval(timmy.current);
			showVideoPlaybackWarning.current = false;
			// @ts-ignore
			timmy.current = null;
		}
	}, [videoElRef.current?.paused]);

	useEffect(() => {
		if (!videoElRef.current?.paused && showVideoPlaybackWarning.current && !playbackWarningShown) {
			showAlert({
				message: 'Could not play video locally',
				description: 'The video is playing normally in the broadcast',
				duration: 7000,
				type: 'warning',
			});

			// send the log
			logger.warn('showing alert for video playback failure');

			// we have shown the alert
			setPlaybackWarningShown(true);

			// ensure we don't have a memory leak
			const timeOut = setInterval(() => {
				setPlaybackWarningShown(false);
				clearInterval(timeOut);
			// show this only every 30 seconds so it doesn't repeat a bunch after start and stopping
			}, 30 * 1000);
		}
	}, [showVideoPlaybackWarning.current]);

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `feed-media-${mediaItem?.id}`, [mediaItem]);

	const syncVidState = useMemo(
		() => presentedMediaItem?.videoState,
		[presentedMediaItem?.videoState],
	);

	const isHlsVideo = useMemo((): boolean => urlOfCurrentPage?.endsWith('.m3u8'), [urlOfCurrentPage]);

	useEffect(() => {
		const asyncGetDuration = async () => {
			// Check if duration_map exists first
			const currentPage = presentedMediaItem?.currentPage ?? 1;
			const durationFromMap = mediaItem?.media_data?.duration_map?.[currentPage - 1];

			const videoElm = document.createElement('video');
			videoElm.preload = 'metadata';
			videoElm.crossOrigin = 'anonymous';
			const extension = urlOfCurrentPage.split('.').pop();
			const mediaType = extension === 'm3u8' ? 'hls' : '';
			let videoDuration = 0;
			if (resourcePool) {
				// Pass durationFromMap as 4th param, resourcePool as 5th param
				videoDuration = durationFromMap ? durationFromMap: await getVideoDuration(urlOfCurrentPage, mediaType, videoElm, resourcePool);
			}
			setDuration(videoDuration);
		};
		asyncGetDuration();
	}, [urlOfCurrentPage, mediaItem?.media_data?.duration_map, presentedMediaItem?.currentPage, resourcePool]);

	const updatePresentedMedia = useCallback((
		funcProps: MediaStateUpdate,
		doEmitChanges: boolean = true,
		from: string = '',
	) => {
		const videoState = { ...funcProps } as VideoState;
		const {
			volume: volumeUpdate,
			muted: mutedUpdate,
			mediaPage: mediaPageUpdate,
		} = funcProps;

		if (volumeUpdate !== undefined || mutedUpdate !== undefined) {
			// Attach the media ID when it doesn't exist on the passed-in object.
			// Example: the default media videos.
			if (mediaPageUpdate) {
				mediaPageUpdate.mediaId = mediaPageUpdate.mediaId ?? mediaId;
			}

			updatePage(
				volumeUpdate,
				mutedUpdate,
				mediaPageUpdate,
				from,
			);
		}

		if (isEditingFeed && presentedMediaItem) {
			if (videoState.paused === false) {
				videoState.timeStarted = new Date();
			}
			updateShowbuilderMedia(presentedMediaItem, videoState);
		} else {
			dispatch(MediaActions.setPresentedMediaVideoState({ mediaId, doEmitChanges, videoState }));
		}
	}, [mediaId, presentedMediaItem, presentedMedia, showbuilderMediaItem]);

	// Update duration when duration_map changes
	useEffect(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const durationFromMap = mediaItem?.media_data?.duration_map?.[currentPage - 1];

		if (durationFromMap !== undefined && durationFromMap > 0) {
			// Update local duration state
			setDuration(durationFromMap);

			// Update videoMachine's duration using dedicated UPDATE_DURATION action
			send(UPDATE_DURATION, {
				duration: durationFromMap,
			});
		}
	}, [mediaItem?.media_data?.duration_map, presentedMediaItem?.currentPage, send]);

	useEchoCancellation(
		videoElRef.current
			? videoElRef : audioElRef,
		echoCancelAudioRef,
	);

	// Apply setSinkId to media elements when echo cancellation is not used
	useEffect(() => {
		if (!doEchoCancel) {
			if (videoElRef.current) {
				applySinkId(videoElRef.current);
			}
			if (audioElRef.current) {
				applySinkId(audioElRef.current);
			}
		}
	}, [applySinkId, videoElRef.current, audioElRef.current, doEchoCancel]);

	const updatePage = (
		updatedVolume?: string,
		updatedMuteState?: boolean,
		mediaPage?: PagedMediaItemExisting | null,
		from?: string,
	) => {
		const meetingIdNumber = meeting?.meeting;
		const updatedVolumeNumber = updatedVolume !== undefined && !Number.isNaN(Number(updatedVolume))
			? Number(updatedVolume)
			: null;

		// Avoid the `0 / null / undefined === false` issue
		const hasVolumeUpdate = updatedVolumeNumber !== null;
		const hasMuteUpdate = updatedMuteState !== undefined;

		if (
			token
			&& meetingIdNumber
			&& session
			&& (hasVolumeUpdate || hasMuteUpdate)
		) {
			let mediaPageErrorStatus: string = 'media page not found, did not backfill';

			if (mediaPage) {
				const mediaPageUpdate: PagedMediaItemExisting = {
					...mediaPage,
					...(hasVolumeUpdate && { volume: updatedVolumeNumber }),
					...(hasMuteUpdate && { muted: updatedMuteState }),
				};

				dispatch(MediaActions.saveMediaPage(
					token,
					meetingIdNumber,
					mediaPageUpdate,
					session,
				));

				mediaPageErrorStatus = '';
			} else if (
				presentedMediaItem?.mediaId
				&& urlOfCurrentPage
				&& from
				&& !['loading video', 'volume onMouseDown'].includes(from)
			) {
				/*
					Logging note:
					This should not happen, but keeping it the same for now
					to ensure this works and look at logs. If no logs are
					being generated, then it can be removed.
				*/
				const mediaPageAdd: PagedMediaItemNew = {
					mediaId: presentedMediaItem.mediaId,
					meeting: meetingIdNumber,
					url: urlOfCurrentPage,
					volume: hasVolumeUpdate ? updatedVolumeNumber : 1,
					muted: hasMuteUpdate ? updatedMuteState : false,
				};

				dispatch(MediaActions.saveMediaPage(
					token,
					meetingIdNumber,
					mediaPageAdd,
					session,
				));

				mediaPageErrorStatus = 'media page not found, but was backfilled';
			}

			// See logging note above
			if (mediaPageErrorStatus) {
				/*
					We expect mediaPage to be found since it's set when the
					media item is uploaded. If it's not found, it would be
					good to know the values used here.
				*/
				logger.error('feed-media.tsx => updatePage => mediaPage not found', {
					meetingIdNumber,
					volume: updatedVolumeNumber,
					muted: updatedMuteState,
					from: from || 'unknown',
					status: mediaPageErrorStatus,
				});
			}
		}
	};

	

	const canApplySyncedChanges = useMemo(() => {
		if (
			(presentedMedia.length > 0 || showbuilderMediaItem)
			&& (currentPageIsVideo || currentPageIsAudio)
			&& readyForInput
			&& !currentMediaControl?.state?.isUseMediaControlsSyncEnabled) {
			return true;
		}
		return false;
	}, [
		presentedMedia.length,
		showbuilderMediaItem,
		urlOfCurrentPage,
		readyForInput,
		syncVidState,
		currentMediaControl?.state?.isUseMediaControlsSyncEnabled,
	]);

	useEffect(() => {
		if (canApplySyncedChanges && syncVidState) {
			if (typeof syncVidState.paused !== 'undefined') {
				if (syncVidState.paused && !paused) {
					logger.debug('feed-media.tsx PAUSE hook b');
					send(PAUSE, { from: 'hook b' });
				} else if (syncVidState.paused === false && paused) {
					logger.debug('feed-media.tsx PLAY hook c', { currentTime: Number(syncVidState?.timeElapsed) });
					send(PLAY, { from: 'hook c', currentTime: Number(syncVidState?.timeElapsed) });
				}
			}
		}
	}, [syncVidState?.paused, paused, canApplySyncedChanges]);

	useEffect(() => {
		if (currentMediaPageVolume && currentMediaPageVolume !== shownVolume) {
			send(VOLUME, {
				shownVolume: currentMediaPageVolume,
				doFireSync: false,
				from: 'hook currentMediaPage.volume',
			});
		}
	}, [currentMediaPageVolume]);

	useEffect(() => {
		const pageMuteState = currentMediaPage?.muted;

		if (pageMuteState !== undefined && pageMuteState !== videoMachineMuted) {
			send(videoMachineMuted ? UNMUTE : MUTE, {
				doFireSync: false,
				from: 'hook currentMediaPage.muted',
			});
		}
	}, [currentMediaPage?.muted]);

	const prevTimeElapsed = usePrevious(syncVidState?.timeElapsed);
	const prevTimeStarted = usePrevious(syncVidState?.timeStarted);
	const isInitialApplicationOfTime = useRef(true);
	useEffect(() => {
		const newStart = syncVidState?.timeStarted !== prevTimeStarted;

		if (!canApplySyncedChanges) { return; }
		// if timeElapsed is undefined, there has not been an update and we start from 0 seconds
		if (!syncVidState?.timeElapsed && syncVidState?.timeElapsed !== 0) { return; }
		// if timeelapsed hasn't changed, there's no update to apply unless it's first load
		if (
			!isInitialApplicationOfTime.current
			&& (syncVidState.timeElapsed === prevTimeElapsed && !newStart)) { return; }

		isInitialApplicationOfTime.current = false;

		const syncedTimeElapsed = Number(syncVidState.timeElapsed);

		if (elapsed < (syncedTimeElapsed - 1.5)
			|| elapsed > (syncedTimeElapsed + 1.5)
			|| (duration - syncedTimeElapsed) < 1.5) {
			logger.debug('feed-media.tsx TIMECHANGE hook f', { currentTime: syncedTimeElapsed });
			send(TIMECHANGE, { currentTime: syncedTimeElapsed, duration, from: 'hook f' });
		}
	}, [
		syncVidState?.timeElapsed,
		syncVidState?.timeStarted,
		canApplySyncedChanges,
		elapsed,
		isInitialApplicationOfTime,
	]);

	/*
		Sometimes, the video machine `pauseVideo` function is called and before
		`paused` is updated to true, the below
			`// if a video pauses when it shouldn't`
		useEffect runs and starts playing the video again. The useEffect could
		be removed, but that also could cause issues. This fix keeps a
		`userPaused` ref up to date with the video machine `paused` value while
		also setting it immediately when a user clicks the play/pause button
		so we can know whether or not to run `videoElRef.current.play();` below.
	*/
	useEffect(() => {
		userPaused.current = paused;
	}, [paused]);

	// Know user initiated pause state immediately in this component
	const setVideoPausedState = (toPaused: boolean): void => {
		if (toPaused) {
			send(PAUSE, { doFireSync: true, from: 'play button' });
			userPaused.current = true;
		} else {
			send(PLAY, { doFireSync: true, from: 'play button' });
			userPaused.current = false;
		}
	};

	useEffect(() => {
		if (videoElRef.current) {
			// if a video pauses when it shouldn't (when unmuting a
			// Chrome browser tab, etc.), resume playback
			videoElRef.current.onpause = async () => {
				// When in editing mode and the user has paused the video
				const editingAndUserPaused = isEditingFeed && userPaused.current;

				// this event also occurs when a video has ended or taken out of the canvas when playing
				if (
					!paused
					&& !videoElRef.current?.ended
					&& stableVideoUrl === videoElRef.current?.src
					&& !syncVidState?.paused
					&& !editingAndUserPaused
				) {
					videoElRef.current?.play();
				}
			};
		}
	}, [videoElRef, stableVideoUrl, paused, syncVidState, roomState]);

	const shouldAutoPlay = useMemo((): boolean => {
		if (roomState === ROOM_STATES.EDITING || isEditingFeed) return false;

		// if the first shot has not started playing yet, this is null
		if (!shotStartTimestampEntity) return false;

		// if syncVidState.paused is true, has been synced and paused previously
		// if syncVidState is undefined, this is initial load/sync for the room
		if (syncVidState?.paused === true) return false;

		return true;
	}, [roomState, shotStartTimestampEntity, syncVidState]);

	/*  this is a duplicate fn in feed-media.tsx,
	*	media-slides-view.tsx, and video-feed-thumbnail.tsx
	*/
	const changePage = useCallback((newPage: number) => {
		console.log('feed-media.tsx changePage', { mediaItem, presentedMediaItem, newPage });

		if (!mediaItem) return;
		const newPageUrl = pageExists(newPage, mediaItem);

		if (
			!presentedMediaItem
			|| !canEditOrControlMedia(mediaItem, user, host, true)
			|| !newPageUrl
		) return;

		// on pagenation, video state is not cleared. We force write new state.
		forceFireSyncOnAutoplay.current = true;

		const newMediaPage = mediaPages.find((page: PagedMediaItemExisting) => page.url === newPageUrl);
		const newVideoState: VideoState = {
			paused: !shouldAutoPlay,
			timeElapsed: 0,
			volume: 1,
		};

		if (newMediaPage) {
			newVideoState.volume = newMediaPage.volume;
			newVideoState.muted = newMediaPage.muted;
		}

		if (isEditingFeed) {
			updateShowbuilderMedia(
				{ ...presentedMediaItem, currentPage: newPage, videoState: newVideoState },
			);
		} else {
			dispatch(MediaActions.setPresentedMediaPage(mediaId, newPage, newVideoState));
		}

		onSelectMediaFeedChange(mediaId);
	}, [
		mediaItem,
		host,
		presentedMediaItem,
		user,
		mediaId,
		shouldAutoPlay,
		isForShotEditorModal,
		currentShot,
		showbuilderMediaItem,
		mediaPages,
	]);

	const [userCanControl, userCanDelete] = useMemo(() => [
		mediaItem ? canEditOrControlMedia(mediaItem, user, host, true, roomState) : false,
		mediaItem ? canDeleteMedia(mediaItem, user, host) : false,
	], [host, mediaItem, user, currentShot]);

	useEffect(() => {
		if (presentedMediaItem && mediaItem && numberOfPages > 1) {
			keyListeners.addListener(keyListeners.KEYS.KEY_LEFT, () => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage((presentedMediaItem?.currentPage ?? 1) - 1);
				}
			});
			keyListeners.addListener(keyListeners.KEYS.KEY_RIGHT, () => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage((presentedMediaItem?.currentPage ?? 1) + 1);
				}
			});
			keyListeners.addListener(keyListeners.KEYS.KEY_UP, () => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage((presentedMediaItem?.currentPage ?? 1) - 1);
				}
			});
			keyListeners.addListener(keyListeners.KEYS.KEY_DOWN, () => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage((presentedMediaItem?.currentPage ?? 1) + 1);
				}
			});
		}
	}, [
		modalOpen,
		isForShotEditorModal,
		mediaModalOpen,
		userCanControl,
		userCanDelete,
		presentedMediaItem,
	]);

	function onMouseMove(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
		if (!scrubBarRef.current) return;

		e.currentTarget?.blur();

		clearTimeout(onMouseMoveDebounce?.current);
		onMouseMoveDebounce.current = setTimeout(() => {
			if (timeBubbleRef.current && scrubBarRef.current) {
				const scrubRect = scrubBarRef.current.getBoundingClientRect();
				const bubbleWidth = timeBubbleRef.current?.offsetWidth ?? 0;
				const time = (e.nativeEvent.offsetX / scrubRect.width) * duration;
				const pos = (e.clientX - scrubRect.left);

				const clampedAndFlooredTime = Math.floor(clamp(time, 0, duration));

				const halfPercWidth = scrubRect.width / 200;
				const clampedPos = Math.floor(clamp(pos, halfPercWidth, scrubRect.width - halfPercWidth));
				const truePos = clampedPos - (bubbleWidth / 2);

				if (mouseoverValue !== clampedAndFlooredTime) {
					setMouseoverValue(clampedAndFlooredTime);
					setBubblePos(truePos);
				}
			}
		}, 1);
	}

	const posterImage = useMemo(() => {
		if (roomState === ROOM_STATES.SHOWTIME) { return ''; }
		return isEditingFeed
			? getCurrentPageThumb(mediaItem?.media_data, presentedMediaItem?.currentPage)
			: urlOfCurrentPage;
	}, [urlOfCurrentPage, roomState, mediaItem, presentedMediaItem, isEditingFeed]);

	const timeScrubCss = useMemo(() => {
		const elapsedPerc = ((elapsed / duration) * 100) + 0.1;
		const bufferedPerc = Math.min(((bufferedDuration / duration) * 100), 100);

		// Three sections: elapsed (white), buffered (medium gray), unbuffered (light gray)
		if (bufferedDuration > elapsed) {
			return {
				background: `linear-gradient(to right, #FFFFFF 0%, #FFFFFF ${elapsedPerc}%, rgb(129 125 136) ${elapsedPerc}%, rgb(129 125 136) ${bufferedPerc}%, rgb(82 80 91) ${bufferedPerc}%, rgb(82 80 91) 100%)`
			};
		}

		// Fallback to two sections if no buffered data
		return {
			background: `linear-gradient(to right, #FFFFFF 0%, #FFFFFF ${elapsedPerc}%, rgb(82 80 91) ${elapsedPerc}%, rgb(82 80 91) 100%)`
		};
	}, [elapsed, duration, bufferedDuration]);

	const volumeScrubCss = useMemo(() => ({ background: `linear-gradient(to right, white 0%, white ${muted ? 0 : shownVolume * 100}%, rgba(255, 255, 255, 0.3) ${muted ? 0 : shownVolume * 100}%, rgba(255, 255, 255, 0.3) 100%)` }), [shownVolume, muted]);

	const onScrubMouseUp = () => {
		logger.debug('feed-media.tsx onScrubMouseUp');
		send(STOPSCRUBBING, { from: 'onScrubMouseUp' });
	};

	const onInput = (e: React.FormEvent<HTMLInputElement>) => {
		e.currentTarget?.blur();
		const inputValue = e.currentTarget?.value;

		clearTimeout(onInputDebounce?.current);
		onInputDebounce.current = setTimeout(() => {
			if (!inputValue) {
				return;
			}
			if (Math.floor(mouseoverValue) !== Math.floor(elapsed)) {
				logger.debug('feed-media.tsx onInput');
				send(SCRUB, { currentTime: mouseoverValue, from: 'onInput' });
				window.addEventListener('mouseup', onScrubMouseUp, { once: true });
			}
		}, 1);
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.currentTarget?.blur();

		clearTimeout(onChangeDebounce.current);
		onChangeDebounce.current = setTimeout(() => {
			logger.debug('feed-media.tsx TIMECHANGE scrubbar onChange', { currentTime: elapsed });
			send(TIMECHANGE, { currentTime: mouseoverValue, duration, from: 'scrubbar onChange' });
		}, 1);
	};

	const isHiddenTwoUpShot = useMemo(
		(): boolean => feedPosition === 0
			&& forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		[forcedLayout, feedPosition],
	);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	const isAutoDurationVideo = useMemo((): boolean => {
		const { feedType } = getShotFeedVideoInfo(
			mediaId,
			media,
			!!audioOnly,
		);

		return autoDuration && feedType === VideoFeedTypes.SingleFeed;
	}, [
		audioOnly,
		autoDuration,
		media,
		mediaId,
	]);

	const isDeck = useMemo(() => {
		if (!mediaItem || !numberOfPages) return false;
		return numberOfPages > 1;
	}, [numberOfPages]);

	/*
		The goal is to keep the video machine context updated with the XState
		context => `elapsed` value and the video duration. We only want this
		when there is a single visible video feed in the current shot.

		We also want to set everything back to 0 when we are not tracking an
		active video, which is what the useEffect cleanup functions are for.

		It probably would have been cleaner to pass this into the state machine,
		but because of the rules of hooks, I think it makes sense to do this
		here instead of `video-machine.ts`. It's also nice to keep everything
		together in one spot.
	*/
	useEffect(() => {
		if (isAutoDurationVideo && !isForMainVideoEditingComponent) {
			videoMachineDispatch({
				type: VideoMachineActionType.SetPlayingOnShotId,
				payload: shotId,
			});
			videoMachineDispatch({
				type: VideoMachineActionType.UpdateDuration,
				payload: duration,
			});
		}

		return () => {
			if (isAutoDurationVideo && !isForMainVideoEditingComponent) {
				videoMachineDispatch({
					type: VideoMachineActionType.SetInitialized,
					payload: false,
				});
			}
		};
	}, [
		shotId,
		duration,
		isAutoDurationVideo,
		videoMachineDispatch,
		isForMainVideoEditingComponent,
	]);

	useEffect(() => {
		if (isAutoDurationVideo && !isForMainVideoEditingComponent) {
			videoMachineDispatch({
				type: VideoMachineActionType.UpdateElapsed,
				payload: elapsed,
			});
		}

		return () => {
			if (isAutoDurationVideo && !isForMainVideoEditingComponent) {
				videoMachineDispatch({
					type: VideoMachineActionType.UpdateElapsed,
					payload: 0,
				});
			}
		};
	}, [
		elapsed,
		isAutoDurationVideo,
		videoMachineDispatch,
		isForMainVideoEditingComponent,
	]);

	useEffect(() => {
		if (presentedMediaItem && (numberOfPages ?? 0) > 1) {
			const previousPage = () => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage((presentedMediaItem?.currentPage ?? 1) - 1);
				}
			};
			const nextPage = () => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage((presentedMediaItem?.currentPage ?? 1) + 1);
				}
			};
			const goToPage = (page: number) => {
				if (
					!mediaModalOpen
				&& (!modalOpen || isForShotEditorModal)
				&& (userCanControl || userCanDelete)) {
					changePage(page);
				}
			};
			addMediaControls({
				id: `${mediaId}`,
				state: {
					...(current as any).state?.context as any,
					isForShotEditorModal,
				},
				previousPage,
				nextPage,
				goToPage,
				from: 'feed-media.tsx - useEffect',
			});
		}
	}, [presentedMediaItem?.currentPage, numberOfPages, mediaId, elapsed]);

	const [forcePoster, setForcePoster] = useState(false);
	
	useEffect(() => {
		if (posterImage && !initialLoad.current && isEditingFeed && !!mediaGeneratedContentTemplate) {
			setForcePoster(true); // if the poster changes we assume the item was edited
		}
	}, [posterImage]);

	useEffect(() => { // will automatically hide the poster if the item is playing
		setForcePoster(false);
	}, [elapsed, paused])
		
	return (
		<>
			{isOver && <div className="dnd-highlighted" />}
					<div
						className={classNames('media', mediaItem?.media_data.fill && 'fill')}
						data-testid='videoMediaControls'
						id='videoMediaControls'
						ref={dropRef}
						style={{
							background: showBackground ? mediaItem?.media_data.background_color : undefined,
						}}
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
						onContextMenu={(e) => {
							if (host) {
								e.preventDefault();
								setRightClickCoords([e.pageX, e.pageY]);
								dispatch(setOpenContextMenuId(contextMenuId));
							}
						}}
					>
						{
						mediaItem?.id
						&& user
						&& (!modalOpen || isForShotEditorModal)
						&& (
							// This portal is to work around a `transform: translateZ(0);` hack on .main-video
							// (the transform makes fixed children elements like the custom context menu fixed
							// relative to it rather than the viewport)
							<Portal targetId="feed-media-context-menu-portal">
								<FeedMediaContextMenu
									mediaItem={mediaItem}
									contextMenuId={contextMenuId}
									clickCoordinates={rightClickCoords}
									socket={socket}
									user={user}
									canEditMedia={canEditOrControlMedia(mediaItem, user, host, false)}
									isForShotEditorModal={isForShotEditorModal}
								/>
							</Portal>
						)}
						{
							<div className="flex-center width-full height-full position-relative">
								{currentPageIsVideo && (
									<>
										<MediaVideo
											id={`media-video-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
											src={stableVideoUrl}
											isHlsVideo={stableIsHlsVideo}
											key={`${'key_FeedMedia_'}${mediaId}${feedPosition}${audioOnly ? '-audio-only' : ''}`}
											className="media-video"
											duration={duration}
											crossOrigin={'anonymous'}
											onLoadStart={() => {
												/*
													Setting volume here since:
													`onLoadStart` happens before `onLoadedData`.
													The goal is the minimize UI jumps.
												*/
												send(VOLUME, {
													shownVolume: currentMediaPageVolume,
													from: 'onLoadStart',
												});
											}}
											onLoadedData={() => {
												setReadyForInput(true);
												iterateLoadedMediaCount();
												// first user to write state, or forced write on page change
												const shouldFire = !syncVidState || forceFireSyncOnAutoplay.current;
												logger.debug('feed-media.tsx LOADED onLoadedData');

												// Use duration from duration_map if available
												const currentPage = presentedMediaItem?.currentPage ?? 1;
												const durationFromMap = mediaItem?.media_data?.duration_map?.[currentPage - 1];
												const videoDuration = (durationFromMap !== undefined && durationFromMap > 0)
													? durationFromMap
													: undefined;

												send(LOADED, {
													video: videoElRef.current,
													audio: doEchoCancel
														? echoCancelAudioRef.current
														: videoElRef.current,
													updatePresentedMedia,
													adjustedVolume,
													shownVolume: currentMediaPageVolume,
													paused: syncVidState?.paused ?? !shouldAutoPlay,
													muted,
													from: 'loading video',
													doFireSync: shouldFire,
													updateMediaControlsContext: addMediaControls,
													mediaId,
													...(currentMediaPage && { currentMediaPage }),
													...(videoDuration && { duration: videoDuration }),
													isForShotEditorModal,
												});
												// VideoMachine will call addMediaControls with updated context including duration
												addMediaControls({
													id: `${mediaId}`,
													state: {
														...(current as any).state?.context as any,
														isForShotEditorModal,
													},
													send,
													from: 'feed-media.tsx - MediaVideo - onLoadedData',
												});

												if (shouldAutoPlay) {
													forceFireSyncOnAutoplay.current = false;
													logger.debug('feed-media.tsx PLAY autoplay', { doFireSync: shouldFire });
													send(PLAY, { doFireSync: shouldFire, from: 'autoplay' });
												}
											}}
											onCanPlayThrough={() => setShowBackground(true)}
											onPlay={() => {
												if (initialLoad.current) { start.current = window.performance.now(); }
											}}
											onTimeUpdate={() => {
												send(TIMING, { from: 'onTimeUpdate' });
												if (initialLoad.current && start.current) {
													initialLoad.current = false;
													const end = window.performance.now();
													const time = (end - start.current) / 1000;
													send(TIMECHANGE, { currentTime: time + elapsed, duration, from: 'onTimeUpdate2' });
												}
											}}
											onEnded={() => {
												logger.debug('feed-media.tsx END onEnded');
												send(END, { from: 'onEnded' });
												if (
													autoDuration
													&& autoplay
													&& shotStartTimestampEntity
													&& roomState === ROOM_STATES.SHOWTIME
												) {
													const currIndex = shots.findIndex(
														(shot: Shot) => shot.shot_id === shotId,
													);
													const nextShot = shots[currIndex + 1];
													if (nextShot) {
														handleSelectShot(nextShot.shot_id);
													}
												}

												// Let the media controls context know the video has ended
												addMediaControls({
													id: `${mediaId}`,
													state: { isForShotEditorModal },
													// @ts-ignore
													paused: true,
													from: 'feed-media.tsx - MediaVideo - onEnded',
												});
											}}
											onError={() => {
												logger.error('feed-media.tsx FAIL', { from: 'onError' });
												send(FAIL, { from: 'onError' });
											}}
											ref={videoElRef}
											posterImage={posterImage}
											loop={!!mediaItem?.media_data.loop}
											elapsed={elapsed}
											forcePoster={forcePoster}
										/>
										{doEchoCancel
											? <audio
												id={`media-audio-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
												ref={echoCancelAudioRef}
												is="advanced-audio"
											></audio>
											: <></>
										}
									</>
								)}

								{currentPageIsAudio && (
									<>
										<MediaAudio
											id={`media-video-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
											key={`${'key_FeedMedia_'}${mediaId}${feedPosition}${audioOnly ? '-audio-only' : ''}`}
											mediaItem={mediaItem}
											duration={duration}
											elapsed={elapsed}
											loop={!!mediaItem?.media_data.loop}
											muted={muted}
											shownVolume={currentMediaPageVolume}
											ref={audioElRef}
											onEnded={() => {
												logger.debug('feed-media.tsx END onEnded');
												send(END, { from: 'onEnded' });
												if (
													autoDuration
													&& autoplay
													&& shotStartTimestampEntity
													&& roomState === ROOM_STATES.SHOWTIME
												) {
													const currIndex = shots.findIndex(
														(shot: Shot) => shot.shot_id === shotId,
													);
													const nextShot = shots[currIndex + 1];
													if (nextShot) {
														handleSelectShot(nextShot.shot_id);
													}
												}

												// Let the media controls context know the video has ended
												addMediaControls({
													id: `${mediaId}`,
													state: { isForShotEditorModal },
													// @ts-ignore
													paused: true,
													from: 'feed-media.tsx - MediaAudio - onEnded',
												});
											}}
											onError={() => {
												logger.error('feed-media.tsx FAIL', { from: 'onError' });
												send(FAIL, { from: 'onError' });
											}}
											onPlay={() => {
												if (initialLoad.current) { start.current = window.performance.now(); }
											}}
											onLoadStart={() => {
												/*
													Setting volume here since:
													`onLoadStart` happens before `onLoadedData`.
													The goal is the minimize UI jumps.
												*/
												send(VOLUME, {
													shownVolume: currentMediaPageVolume,
													from: 'onLoadStart',
												});
											}}
											onLoadedData={() => {
												setReadyForInput(true);
												iterateLoadedMediaCount();
												// first user to write state, or forced write on page change
												const shouldFire = !syncVidState || forceFireSyncOnAutoplay.current;
												logger.debug('feed-media.tsx LOADED Audio onLoadedData');

												// Use duration from duration_map if available
												const currentPage = presentedMediaItem?.currentPage ?? 1;
												const durationFromMap = mediaItem?.media_data?.duration_map?.[currentPage - 1];
												const audioDuration = (durationFromMap !== undefined && durationFromMap > 0)
													? durationFromMap
													: undefined;

												//mediaId == 8655,
												// but currentMediaPage is 5653
												send(LOADED, {
													video: audioElRef.current,
													audio: doEchoCancel
														? echoCancelAudioRef.current
														: audioElRef.current,
													updatePresentedMedia,
													adjustedVolume,
													shownVolume: currentMediaPageVolume,
													paused: syncVidState?.paused ?? !shouldAutoPlay,
													muted,
													from: 'loading video',
													doFireSync: shouldFire,
													updateMediaControlsContext: addMediaControls,
													mediaId,
													...(currentMediaPage && { currentMediaPage }),
													...(audioDuration && { duration: audioDuration }),
													isForShotEditorModal,
												});
												addMediaControls({
													id: `${mediaId}`,
													state: {
														...(current as any).state?.context as any,
														isForShotEditorModal,
													},
													send,
													from: 'feed-media.tsx - MediaAudio - onLoadedData',
												});

												if (shouldAutoPlay) {
													forceFireSyncOnAutoplay.current = false;
													logger.debug('feed-media.tsx PLAY Audio autoplay', { doFireSync: shouldFire });
													send(PLAY, { doFireSync: shouldFire, from: 'autoplay' });
												}
											}}
											onTimeUpdate={() => {
												send(TIMING, { from: 'onTimeUpdate' });
												if (initialLoad.current && start.current) {
													initialLoad.current = false;
													const end = window.performance.now();
													const time = (end - start.current) / 1000;
													send(TIMECHANGE, { currentTime: time + elapsed, duration, from: 'onTimeUpdate2' });
												}
											}}
										/>
										{doEchoCancel
											? <audio
												id={`media-audio-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
												ref={echoCancelAudioRef}
                								is="advanced-audio"
											></audio>
											: <></>
										}
									</>
								)}

								{!currentPageIsVideo && !currentPageIsAudio && (
									<img
										className="media-img"
										src={urlOfCurrentPage ?? ''}
										alt={''}
										onLoad={() => {
											setShowBackground(true);
											iterateLoadedMediaCount();
										}}
										ref={imgRef}
									/>
								)}
							</div >
						}
					</div>
			{
				mediaItem
				&& !globalIsDragging
				&& canEditOrControlMedia(mediaItem, user, host, true, roomState)
				&& <Portal
					targetId={isForMainVideoEditingComponent ? 'controls-container-editing' : 'controls-container-live'}
					className={classNames(
						`media-controls-layer width-full height-full ${videoGridClasses[feedPosition]}`,
						{
							hover: isHovering,
							'hidden-feed': isHiddenTwoUpShot,
							'display-none': audioOnly,
						},
					)}
				>
					<div
						className="flex width-full"
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
					>
						{!currentPageIsVideo
							&& <>
								<div className='width-full align-items-center presentation-controls'>
									{presentedMediaItem && numberOfPages > 1
										&& <button
											className='background-lighter round padding-medium control'
											data-testid="slideDeckMediaPresentationsControlLeftVisible"
											disabled={presentedMediaItem?.currentPage === 1} onClick={(e) => {
												changePage(presentedMediaItem.currentPage - 1);
												e.currentTarget.blur();
											}}>
											<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={9} color={COLORS.GRAY} />
										</button>
									}
									<div className="controls-pages">
										{
										presentedMediaItem
										&& presentedMediaItem.currentPage
										&& numberOfPages > 1
										&& <span>{presentedMediaItem?.currentPage}/{numberOfPages}</span>
										}
									</div>
									{presentedMediaItem && numberOfPages > 1
										&& <button
											onClick={(e) => {
												changePage(presentedMediaItem.currentPage + 1);
												e.currentTarget.blur();
											}}
											className='background-lighter round padding-medium control'
											disabled={presentedMediaItem?.currentPage === numberOfPages}
											data-testid="slideDeckMediaPresentationsControlRightVisible"
										>
											<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={9} color={COLORS.GRAY} />
										</button>
									}
								</div>
							</>
						}
						{currentPageIsVideo
							&& <>
								<div className="width-full align-items-center media-controls" data-testid='videoMediaControlsVisible'>
									{presentedMediaItem && numberOfPages > 1
										&& <>
											<button
												onClick={(e) => {
													changePage(presentedMediaItem.currentPage - 1);
													e.currentTarget.blur();
												}}
												className='height-full control left'
												disabled={presentedMediaItem.currentPage === 1}
											>
												<Icon
													name={ICONS.KEYBOARD_ARROW_LEFT}
													size={9}
													color={presentedMediaItem.currentPage === 1
														? COLORS.GRAY
														: COLORS.WHITE}
												/>
											</button>
										</>
									}
									<PlayButton
										paused={paused}
										videoPlayPause={setVideoPausedState}
										className={'play-button-canvas'}
										isForShotEditorModal={isForShotEditorModal}
									/>
									<MediaVideoTimer duration={duration} elapsed={elapsed} />

									<div className={
										classNames(
											'align-items-center position-relative flex-grow scrub-wrap',
											{
												presenter: !host,
												deck: isDeck,
											},
										)}
									>

											<div
												ref={timeBubbleRef}
												className='time-bubble'
												style={{ left: bubblePos }}
											>
												{getVideoTimeAsHHMMSS(mouseoverValue)}
											</div>

										<input
											type="range"
											className="scrubbar"
											onChange={onChange}
											onInput={onInput}
											onMouseMove={onMouseMove}
											value={elapsed}
											style={timeScrubCss}
											max={mediaItem?.media_data?.duration_map?.[0] ?? duration}
											min={0}
											step={0.02}
											ref={scrubBarRef}
										/>
									</div>
									{<div className={'align-items-center position-relative padding-left-medium vol-wrap'}>
										{!isForShotEditorModal
											? <div className={classNames('vol-icon-wrap', { muted })} onClick={() => {
												logger.debug(`feed-media.tsx ${muted ? 'UNMUTE' : 'MUTE'} volume button`, { doFireSync: true });
												send(muted ? UNMUTE : MUTE, {
													doFireSync: true,
													from: 'volume button',
													currentMediaPage,
												});
											}}>
												<Icon className='margin-right-small' name={muted ? ICONS.AUDIO_MUTED : ICONS.SOUND} size={18} color={muted ? COLORS.GRAY : COLORS.WHITE} />
											</div>
											: <TooltipWrapper message="Cannot unmute videos in Edit Shot view">
												<Icon
													className='margin-right-small margin-top-small'
													name={muted ? ICONS.AUDIO_MUTED : ICONS.SOUND}
													size={18}
													color={muted ? COLORS.GRAY : COLORS.WHITE}
												/>
											</TooltipWrapper>
										}
										{!isForShotEditorModal
											&& <input
												type="range"
												className="volume"
												onMouseDown={() => {
													logger.debug('feed-media.tsx VOLUME onMouseDown', { volume, doFireSync: true });
													send(VOLUME, {
														shownVolume,
														doFireSync: true,
														from: 'volume onMouseDown',
														currentMediaPage,
													});
												}}
												onChange={(e) => {
													logger.debug('feed-media.tsx VOLUME onChange', { volume, doFireSync: true });
													send(VOLUME, { shownVolume: Number(e.target.value), from: 'volume onChange' });
													e.currentTarget.blur();
												}}
												onMouseUp={(e) => {
													logger.debug('feed-media.tsx VOLUME onMouseUp', { volume, doFireSync: true });
													send(VOLUME, {
														shownVolume,
														doFireSync: true,
														from: 'volume onMouseUp',
														currentMediaPage,
													});
													e.currentTarget.blur();
												}}
												value={muted ? 0 : shownVolume}
												step={0.01}
												min={0}
												max={1}
												style={volumeScrubCss}
											>
											</input>
										}
									</div>}
									{presentedMediaItem && numberOfPages > 1
										&& <>
											<button
												onClick={(e) => {
													changePage(presentedMediaItem.currentPage + 1);
													e.currentTarget.blur();
												}}
												className='height-full control right'
												disabled={
													presentedMediaItem?.currentPage === numberOfPages
												}
											>
												<Icon
													name={ICONS.KEYBOARD_ARROW_RIGHT}
													size={9}
													color={
														presentedMediaItem?.currentPage === numberOfPages
															? COLORS.GRAY
															: COLORS.WHITE
													}
												/>
											</button>
											<div className="controls-pages">
												{
													presentedMediaItem.currentPage
													&& numberOfPages > 1
													&& <span>
															{presentedMediaItem?.currentPage}/{numberOfPages}
													</span>
												}
											</div>
										</>
									}
								</div>
							</>
						}
						{mediaGeneratedContentTemplate && host && (
							<Portal targetId="video-hover-options-portal">
								<VideoHoverOptions
									mediaItem={mediaItem}
									generateContentTemplate={mediaGeneratedContentTemplate}
									className={classNames(
										'width-full',
										'height-full',
										videoGridClasses[feedPosition],
										{
											'hidden-feed': isHiddenTwoUpShot,
											'display-none': audioOnly,
										},
									)}
									isHovering={isHovering}
									videoRef={videoElRef.current ?? imgRef.current}
									isForShotEditorModal={isForShotEditorModal}
									setShotModalStylepackDrawer={setShotModalStylepackDrawer}
								/>
							</Portal>
						)}
					</div>
				</Portal>
			}
		</>
	);
}
